<template>
  <vue-html2pdf
    :id="id || undefined"
    ref="invoiceSummaryHtml2Pdf"
    :show-layout="false"
    :float-layout="true"
    :enable-download="false"
    :preview-modal="true"
    :paginate-elements-by-height="1400"
    :filename="filename"
    :pdf-quality="2"
    :manual-pagination="false"
    pdf-format="a4"
    pdf-orientation="portrait"
    pdf-content-width="800px"
    @hasStartedGeneration="onHasStartedGeneration"
    @hasGenerated="onHasGenerated"
  >
    <section
      slot="pdf-content"
      dir="rtl"
      class="pdf-content invoice-summary-print-root"
    >
      <header class="invoice-header">
        <div>
          <div class="hospital-name">
            {{ $brand.nameArWithNumber }}
          </div>
          <div class="invoice-title">
            {{ $t('common.invoice-summary') }}
          </div>
        </div>

        <div class="header-meta">
          <div class="meta-row">
            <span class="label">{{ $t('common.date') }}:</span>
            <span>{{ formattedPrintDate }}</span>
          </div>
          <div class="meta-row">
            <span class="label">{{ $t('common.total-invoices') }}:</span>
            <span>{{ invoices.length }}</span>
          </div>
        </div>
      </header>

      <table class="summary-table invoice-table">
        <thead>
          <tr>
            <th>{{ $t('hospital.invoice-no') }}</th>
            <th>{{ $t('common.patient-name') }}</th>
            <th>{{ $t('reception.service') }}</th>
            <th class="amount">
              {{ $t('employee.amount') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="inv in invoices"
            :key="inv.id"
          >
            <td>{{ inv.invoice_no }}</td>
            <td>{{ getPatientName(inv) }}</td>
            <td>{{ getServiceTitle(inv) }}</td>
            <td class="amount">
              {{ formatPrice(getNetAmount(inv)) }}
            </td>
          </tr>
        </tbody>
      </table>

      <section class="invoice-summary">
        <div class="summary-row total">
          <span>{{ $t('common.total') }}</span>
          <span>
            {{ formatPrice(computedTotalAmount) }}
          </span>
        </div>
      </section>

      <footer class="invoice-footer">
        <div>{{ $t('common.printed-on') }}: {{ fullTimestamp }}</div>
      </footer>
    </section>
  </vue-html2pdf>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf'
import { getNestedProperty } from '@/utils/nestedProperty'

export default {
  name: 'InvoiceSummaryPrint',
  components: { VueHtml2pdf },

  props: {
    id: {
      type: String,
      default: '',
    },
    invoices: {
      type: Array,
      default: () => [],
    },
    totalAmount: {
      type: Number,
      default: 0,
    },
    invoiceType: {
      type: String,
      default: 'general',
    },
    printDate: {
      type: [String, Date],
      default: () => new Date(),
    },
    filename: {
      type: String,
      default: 'invoice-summary',
    },
  },

  computed: {
    formattedPrintDate() {
      return this.formatToYMD(new Date(this.printDate))
    },

    fullTimestamp() {
      return new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },

    computedTotalAmount() {
      if (!Array.isArray(this.invoices) || this.invoices.length === 0) {
        return this.totalAmount
      }

      const hasDiscount = this.invoices.some(inv => Number(inv.discount) > 0)
      if (!hasDiscount) {
        return this.totalAmount
      }

      return this.invoices.reduce(
        (sum, inv) => sum + this.getNetAmount(inv),
        0,
      )
    },
  },

  methods: {
    formatPrice(price) {
      if (price === null || price === undefined || price === '') return '0 IQD'
      return `${Number(price).toLocaleString()} IQD`
    },

    formatToYMD(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '----/--/--'
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}/${m}/${d}`
    },

    getPatientName(invoice) {
      if (this.invoiceType === 'test') {
        return getNestedProperty(invoice, 'patient_card.patient.name', 'N/A')
      }
      return getNestedProperty(
        invoice,
        'patient_card.patient.name',
        getNestedProperty(
          invoice,
          'patient_card_incubator.patient_card.patient.name',
          'N/A',
        ),
      )
    },

    getServiceTitle(invoice) {
      if (this.invoiceType === 'test') {
        return getNestedProperty(invoice, 'patient_card_test.test.name', 'N/A')
      }
      if (this.invoiceType === 'nicu') {
        return getNestedProperty(invoice, 'patient_card_incubator.incubator.name', 'N/A')
      }
      if (this.invoiceType === 'surgery') {
        return getNestedProperty(
          invoice,
          'surgery.title',
          getNestedProperty(invoice, 'surgery.name', 'N/A'),
        )
      }
      return getNestedProperty(invoice, 'service.title', 'N/A')
    },

    getNetAmount(invoice) {
      if (!invoice) return 0
      const amount = Number(invoice.amount) || 0
      const discount = Number(invoice.discount) || 0
      const net = amount - discount
      return net > 0 ? net : 0
    },

    onHasStartedGeneration() {
      this.$emit('hasStartedGeneration')
    },

    onHasGenerated(event) {
      this.$emit('hasGenerated', event)
    },

    generatePdf() {
      this.$refs.invoiceSummaryHtml2Pdf?.generatePdf()
    },
  },
}
</script>

<style scoped>
@font-face {
  font-family: "TahomaPDF";
  src: url("/fonts/Tahoma.ttf") format("truetype");
  font-weight: normal;
  font-style: normal;
}

.pdf-content {
  font-family:
    "TahomaPDF",
    "Tahoma",
    "Segoe UI",
    "Arial",
    "Noto Naskh Arabic",
    "Amiri",
    sans-serif;

  color: #000;
  background: #fff;
  padding: 30px;

  direction: rtl;
  unicode-bidi: embed;
  letter-spacing: normal;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 2px solid #000;
  padding-bottom: 12px;
  margin-bottom: 20px;
}

.hospital-name {
  font-size: 14px;
  font-weight: bold;
}

.invoice-title {
  font-size: 22px;
  font-weight: bold;
  margin-top: 4px;
}

.header-meta {
  font-size: 13px;
}

.meta-row {
  display: flex;
  gap: 6px;
}

.label {
  font-weight: bold;
}

.summary-table,
.invoice-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.summary-table th,
.summary-table td,
.invoice-table th,
.invoice-table td {
  border: 1px solid #000;
  padding: 8px;
  font-size: 13px;
  text-align: right;
}

.summary-table th,
.invoice-table th {
  font-weight: bold;
}

.amount {
  text-align: left;
  direction: ltr;
  font-weight: bold;
}

.invoice-summary {
  margin-top: 20px;
  border-top: 2px solid #000;
  padding-top: 10px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.summary-row.total {
  font-weight: bold;
  margin-top: 6px;
  padding-top: 10px;
}

.invoice-footer {
  margin-top: 30px;
  border-top: 1px solid #000;
  padding-top: 10px;
  text-align: center;
  font-size: 12px;
}

@media print {
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
